import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/ui-custom/Reveal";
import { Seo } from "@/components/seo/Seo";
import { BrandCard } from "@/components/brands/BrandCard";
import { CTABand } from "@/components/home/CTABand";
import { SERVICE_LIST } from "@/content/services";

const CHANNELS = [["", "Retail & foodservice"], ["retail", "Retail"], ["foodservice", "Foodservice"]];

export default function Brands() {
  const [params, setParams] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const origin = params.get("origin") || "";
  const channel = params.get("channel") || "";
  const newOnly = params.get("new") === "1";
  const hasFilters = !!(q || category || origin || channel || newOnly);

  useEffect(() => {
    Promise.all([api.get("/brands"), api.get("/categories"), api.get("/origins")]).then(([b, c, o]) => { setBrands(b.data); setCats(c.data); setOrigins(o.data); setLoaded(true); });
  }, []);

  const update = (patch) => {
    const next = Object.fromEntries(params.entries());
    Object.entries(patch).forEach(([k, v]) => (v ? (next[k] = v) : delete next[k]));
    setParams(next, { replace: true });
  };

  const catMap = Object.fromEntries(cats.map((c) => [c.slug, c.name]));
  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return brands.filter((b) => {
      if (category && !b.categories.includes(category)) return false;
      if (origin && !(b.origin || "").toLowerCase().endsWith(origin.toLowerCase())) return false;
      if (channel && !(b.suitable_for || []).includes(channel)) return false;
      if (newOnly && !b.is_new) return false;
      if (!s) return true;
      const hay = [b.name, b.tagline, b.description, b.origin, ...(b.products || []).map((p) => p.name), ...b.categories.map((c) => catMap[c] || "")].join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [brands, q, category, origin, channel, newOnly, catMap]);

  return (
    <main data-testid="brands-page">
      <Seo
        title="Wholesale Food Ranges & Brands | Sydney Food Distributor"
        description="Browse the wholesale food ranges distributed by Deliver in Sydney: deli meats, smallgoods, cheese, antipasto, olive oil, pasta, sauces, frozen and more. Download range catalogues and enquire."
        noindex={hasFilters}
        canonical={`${process.env.REACT_APP_BACKEND_URL}/brands`}
      />
      <section className="pt-32 sm:pt-40 pb-10 mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Reveal><p className="eyebrow text-[var(--terracotta)] mb-5">Brands & range</p></Reveal>
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <Reveal delay={0.05} className="lg:col-span-7"><h1 className="font-display font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[var(--ink)]">Wholesale food ranges for Sydney retailers and kitchens</h1></Reveal>
          <Reveal delay={0.15} className="lg:col-span-5"><p className="text-base md:text-lg text-[var(--charcoal)] font-light leading-relaxed" data-testid="brands-intro">Every range Deliver distributes has its own page with imagery, a written overview and a downloadable PDF catalogue listing the full product range. Search by brand, product or category, or browse by <Link to="/range/sliced-meats-retail-deli" className="link-underline text-[var(--ink)]">deli meats</Link>, <Link to="/range/cheese" className="link-underline text-[var(--ink)]">cheese</Link>, <Link to="/range/oils-and-vinegars" className="link-underline text-[var(--ink)]">olive oil</Link> and <Link to="/range/pasta-flour-and-rice" className="link-underline text-[var(--ink)]">pasta</Link>.</p></Reveal>
        </div>
      </section>

      <div className="sticky top-[72px] z-30 bg-[rgba(255,252,242,0.94)] backdrop-blur-md border-y border-[var(--sand)]" data-testid="brands-filter-bar">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-3 flex flex-col lg:flex-row lg:items-center gap-3">
          <label className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input value={q} onChange={(e) => update({ q: e.target.value })} placeholder="Search brand, product or category" className="w-full bg-white border border-[var(--sand)] rounded-full pl-11 pr-10 py-2.5 text-sm outline-none focus:border-[var(--ink)] transition-colors" data-testid="brand-search-input" />
            {q && <button onClick={() => update({ q: "" })} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)]" data-testid="brand-search-clear"><X size={14} /></button>}
          </label>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 lg:mx-0 lg:px-0 lg:flex-wrap">
            <button onClick={() => update({ category: "" })} className={`chip ${!category ? "active" : ""}`} data-testid="chip-all">All</button>
            {cats.filter((c) => c.brand_count > 0).map((c) => (
              <button key={c.slug} onClick={() => update({ category: category === c.slug ? "" : c.slug })} className={`chip ${category === c.slug ? "active" : ""}`} data-testid={`chip-${c.slug}`}>{c.name}</button>
            ))}
          </div>
        </div>
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pb-3 flex flex-wrap items-center gap-2 text-sm">
          <select value={origin} onChange={(e) => update({ origin: e.target.value })} className="bg-white border border-[var(--sand)] rounded-full px-4 py-1.5 text-xs outline-none focus:border-[var(--ink)]" data-testid="brand-origin-select">
            <option value="">All origins</option>
            {origins.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={channel} onChange={(e) => update({ channel: e.target.value })} className="bg-white border border-[var(--sand)] rounded-full px-4 py-1.5 text-xs outline-none focus:border-[var(--ink)]" data-testid="brand-channel-select">
            {CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={() => update({ new: newOnly ? "" : "1" })} className={`chip !text-xs ${newOnly ? "active" : ""}`} data-testid="chip-new-only">New ranges</button>
          <span className="ml-auto text-xs text-[var(--muted)]" data-testid="brands-result-count">{shown.length} {shown.length === 1 ? "range" : "ranges"}</span>
          {hasFilters && <button onClick={() => setParams({}, { replace: true })} className="text-xs link-underline text-[var(--ink)]" data-testid="brands-clear-filters">Clear filters</button>}
        </div>
      </div>

      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-10 lg:py-14 min-h-[50vh]">
        {loaded && shown.length === 0 ? (
          <div className="py-20 text-center" data-testid="brands-empty-state">
            <p className="font-display text-3xl text-[var(--ink)]">No ranges match your search</p>
            <p className="mt-3 text-[var(--charcoal)] font-light">Try another term, or <Link to="/contact" className="link-underline">ask our team</Link> about sourcing a product.</p>
            <button onClick={() => setParams({}, { replace: true })} className="btn-pill btn-outline mt-6">Show all ranges</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6" data-testid="brands-grid">
            {shown.map((b, i) => <BrandCard key={b.id} brand={b} categoryMap={catMap} index={i % 8} />)}
          </div>
        )}
      </section>

      <section className="bg-[var(--cream-2)] py-16 lg:py-20">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal><p className="eyebrow text-[var(--terracotta)]">Browse by category</p><h2 className="font-display text-3xl sm:text-4xl mt-3 text-[var(--ink)]">Wholesale categories</h2><p className="mt-4 text-[var(--charcoal)] font-light leading-relaxed">Each category page explains what Deliver supplies in that area, who it suits and which ranges are available, from wholesale deli meats and smallgoods to Italian pantry, dairy and frozen.</p></Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <ul className="grid sm:grid-cols-2 gap-x-8" data-testid="brands-category-index">
                {cats.filter((c) => c.brand_count > 0).map((c) => (
                  <li key={c.slug} className="border-b border-[var(--sand)]"><Link to={`/range/${c.slug}`} className="flex items-center justify-between py-3 text-[var(--ink)] hover:text-[var(--terracotta)] transition-colors group"><span>{c.name}</span><ArrowRight size={14} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" /></Link></li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-2">
                {SERVICE_LIST.map((s) => <Link key={s.slug} to={s.path} className="chip !text-xs" data-testid={`brands-service-${s.slug}`}>{s.navLabel}</Link>)}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABand />
    </main>
  );
}
